// Scroll split hero (inspired by componentry.dev "Scroll Split Card"), plain React + CSS.
// The hero art is shown in three vertical panels that line up seamlessly. While the visitor
// scrolls, the hero pauses under the header, the panels separate, then flip one after another
// to reveal three cards (backs). Reduced-motion visitors get the plain hero.
//
// <PtSplitHero backs={[{ title, text, tone, onClick }]}> hero art children </PtSplitHero>
(function () {
  const { useState, useEffect, useRef } = React;
  const h = React.createElement;
  const clamp = (v) => Math.max(0, Math.min(1, v));
  const ease = (t) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);

  function PtSplitHero({ backs, children }) {
    const wrap = useRef(null), stage = useRef(null);
    const [p, setP] = useState(0);            // scroll progress through the pinned range, 0..1
    const [w, setW] = useState(0);            // stage width (each panel shows a third of it)
    const [heroH, setHeroH] = useState(470);
    const [pin, setPin] = useState(0);        // header height: the hero pins just below it
    const [extra, setExtra] = useState(0);    // scroll distance used by the animation
    const calm = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    useEffect(() => {
      if (calm) return;
      let raf = 0;
      const measure = () => {
        if (!stage.current) return;
        setW(stage.current.clientWidth);
        const art = stage.current.querySelector(".split-art");
        if (art) setHeroH(art.offsetHeight);
        const nav = document.querySelector(".nav");
        setPin(nav ? Math.round(nav.getBoundingClientRect().height) : 0);
        setExtra(Math.round(window.innerHeight * (window.innerWidth < 768 ? 0.75 : 1.1)));
      };
      const onScroll = () => {
        if (raf) return;
        raf = requestAnimationFrame(() => {
          raf = 0;
          if (!wrap.current) return;
          const top = wrap.current.getBoundingClientRect().top;
          const pinTop = parseFloat(getComputedStyle(wrap.current.firstChild).top) || 0;
          const range = wrap.current.offsetHeight - wrap.current.firstChild.offsetHeight;
          setP(range > 0 ? clamp((pinTop - top) / range) : 0);
          // Keep the cloud parallax working in all three panels (same offset, so seams line up).
          // Frozen while pinned, so clouds stay put during the split.
          const pinStart = top + window.scrollY - pinTop;
          const py = Math.min(window.scrollY, Math.max(0, pinStart)) * 0.35;
          stage.current && stage.current.querySelectorAll(".clouds").forEach((c) => { c.style.transform = "translateY(" + py.toFixed(1) + "px)"; });
        });
      };
      measure();
      onScroll();
      window.addEventListener("resize", measure);
      window.addEventListener("scroll", onScroll, { passive: true });
      return () => { window.removeEventListener("resize", measure); window.removeEventListener("scroll", onScroll); if (raf) cancelAnimationFrame(raf); };
    }, []);

    if (calm) return h("div", { className: "hero-main" }, children);

    // Phase 1 (0 - .4): separate. Phase 2 (.35 - .95): flip, panel by panel.
    const sep = ease(clamp(p / 0.4));
    const gap = (w < 600 ? 10 : 22) * sep;
    const third = w / 3;
    const pw = third - (2 * gap) / 3;                        // panels shrink as they part, so the row stays inside the page
    const panels = [0, 1, 2].map((i) => {
      const flip = ease(clamp((p - 0.35 - i * 0.08) / 0.44));
      const r = 28 * (1 - sep) + 22 * sep;                  // outer corners stay round, inner corners open up
      const radius = [i === 0 ? 28 : r * sep, i === 2 ? 28 : r * sep, i === 2 ? 28 : r * sep, i === 0 ? 28 : r * sep].map((x) => x.toFixed(1) + "px").join(" ");
      return {
        style: {
          left: Math.round(i * (pw + gap)),
          width: Math.round(pw) + (i < 2 && gap < 1 ? 1 : 0),   // 1px overlap at rest hides the seams
          transform: "translateY(" + (-10 * sep * (i === 1 ? 1 : 0.4)).toFixed(1) + "px) rotateY(" + (180 * flip).toFixed(1) + "deg)",
          borderRadius: radius,
          zIndex: flip > 0.5 ? 3 - i : 1,
        },
        faceRadius: radius,
        flipped: flip > 0.5,
      };
    });

    return h("div", { ref: wrap, className: "split-hero", style: { height: heroH + extra } },
      h("div", { className: "split-sticky", style: { top: pin } },
        h("div", { ref: stage, className: "split-stage", style: { height: heroH } },
          panels.map((pn, i) => {
            const b = backs[i] || {};
            return h("div", { key: i, className: "split-panel", style: pn.style },
              h("div", { className: "split-face split-front", style: { borderRadius: pn.faceRadius }, "aria-hidden": i === 0 ? undefined : "true" },
                h("div", { className: "hero-main split-art", style: { width: w || "300%", left: -i * third } }, children)
              ),
              h(b.onClick ? "button" : "div", {
                type: b.onClick ? "button" : undefined,
                className: "split-face split-back " + (b.tone || ""),
                style: { borderRadius: pn.faceRadius },
                tabIndex: pn.flipped ? 0 : -1,
                "aria-hidden": pn.flipped ? undefined : "true",
                onClick: b.onClick,
              },
                b.kicker ? h("span", { className: "split-kicker" }, b.kicker) : null,
                h("span", { className: "split-title" }, b.title),
                b.text ? h("span", { className: "split-text" }, b.text) : null,
                b.onClick ? h("span", { className: "split-go", "aria-hidden": "true" }, h(Icon.ArrowUpRight, null)) : null
              )
            );
          })
        )
      )
    );
  }

  window.PtSplitHero = PtSplitHero;
})();
